
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

const client = createClient(supabaseUrl, supabaseKey);

// Generic Entity Helper
const createEntity = (table) => ({
    get: async (id) => {
        const { data, error } = await client.from(table).select('*').eq('id', id).single();
        if (error) throw error;
        return data;
    },
    select: (cols = '*') => client.from(table).select(cols),
    filter: async (filters = {}, order = null, limit = null) => {
        let query = client.from(table).select('*');
        Object.entries(filters).forEach(([k, v]) => {
            query = query.eq(k, v);
        });
        if (order) {
            if (order.startsWith('-')) query = query.order(order.substring(1), { ascending: false });
            else query = query.order(order, { ascending: true });
        }
        if (limit) query = query.limit(limit);
        const { data, error } = await query;
        if (error) throw error;
        return data || [];
    },
    create: async (payload) => {
        const { data, error } = await client.from(table).insert(payload).select().single();
        if (error) throw error;
        return data;
    },
    update: async (id, payload) => {
        const { data, error } = await client.from(table).update(payload).eq('id', id).select().single();
        if (error) throw error;
        return data;
    },
    delete: async (id) => {
        const { error } = await client.from(table).delete().eq('id', id);
        if (error) throw error;
        return true;
    },
    paginate: async ({ page = 1, pageSize = 15, filters = {}, searchOr = null, orderBy = null }) => {
        const p = parseInt(page);
        const ps = parseInt(pageSize);
        const from = (p - 1) * ps;
        const to = from + ps - 1;

        let query = client.from(table).select('*', { count: 'exact' });

        // Apply filters
        Object.entries(filters).forEach(([k, v]) => {
            if (v === undefined || v === null || v === 'todos' || v === 'todas') return;

            if (typeof v === 'object' && v.op && v.value !== undefined) {
                // Handle complex filter: { op: 'lt', value: '...' }
                switch (v.op) {
                    case 'lt': query = query.lt(k, v.value); break;
                    case 'lte': query = query.lte(k, v.value); break;
                    case 'gt': query = query.gt(k, v.value); break;
                    case 'gte': query = query.gte(k, v.value); break;
                    case 'neq': query = query.neq(k, v.value); break;
                    case 'like': query = query.like(k, v.value); break;
                    case 'ilike': query = query.ilike(k, v.value); break;
                    case 'in': query = query.in(k, v.value); break; // value should be array
                    default: query = query.eq(k, v.value);
                }
            } else {
                // Simple equality
                query = query.eq(k, v);
            }
        });

        // Apply OR search if present (e.g. "descricao.ilike.%term%,cliente.ilike.%term%")
        if (searchOr) {
            query = query.or(searchOr);
        }

        // Order
        if (orderBy) {
            // orderBy can be string "col" or "-col" or object { column, ascending }
            if (typeof orderBy === 'string') {
                if (orderBy.startsWith('-')) query = query.order(orderBy.substring(1), { ascending: false });
                else query = query.order(orderBy, { ascending: true });
            } else {
                query = query.order(orderBy.column, { ascending: orderBy.ascending });
            }
        }

        query = query.range(from, to);

        const { data, error, count } = await query;
        if (error) throw error;
        return { data: data || [], count: count || 0 };
    }
});

export const agrogestao = {
    client: client,
    auth: {
        me: async () => {
            const { data: { user } } = await client.auth.getUser();
            return user;
        },
        signOut: async () => {
            return client.auth.signOut();
        },
        getSession: () => client.auth.getSession(),
        signInWithPassword: (credentials) => client.auth.signInWithPassword(credentials),
        signUpWithPassword: (credentials) => client.auth.signUp(credentials),
        signInWithOtp: (payload) => client.auth.signInWithOtp(payload),
        resetPasswordForEmail: (email, redirectTo) => client.auth.resetPasswordForEmail(email, { redirectTo }),
        updatePassword: (password) => client.auth.updateUser({ password })
    },
    functions: {
        invoke: async (name, body) => {
            try {
                const { data: { session } } = await client.auth.getSession();
                if (!session) {
                    return { data: { success: false, error: 'Sessão necessária' } };
                }
                const { data, error } = await client.functions.invoke(name, { body });
                if (error) throw error;
                return { data };
            } catch (err) {
                if (err.status === 401) {
                    return { data: { success: false, error: 'Sessão expirada' } };
                }
                throw err;
            }
        }
    },
    access: {
        logAudit: async (payload) => {
            try {
                const { data: { session } } = await client.auth.getSession();
                if (!session?.user) return; // Skip if no session

                await client.from('audit_logs').insert({
                    user_email: session.user.email,
                    ...payload,
                    created_at: new Date().toISOString()
                });
            } catch (e) {
                // Ignore audit errors to prevent UI disruption
            }
        }
    },
    entities: {
        Propriedade: createEntity('propriedades'),
        Licenca: createEntity('licencas'),
        Talhao: createEntity('talhoes'),
        Safra: createEntity('safras'),
        Lancamento: createEntity('lancamentos'),
        Insumo: createEntity('insumos'),
        MovimentacaoInsumo: createEntity('movimentacoes_insumo'),
        Animal: createEntity('animais'),
        MovimentoAnimal: createEntity('movimentos_animais'),
        PesagemAnimal: createEntity('pesagens_animais'),
        ManejoAnimal: createEntity('manejos_animais'),
        ContaPagar: createEntity('contas_pagar'),
        ContaReceber: createEntity('contas_receber'),
        CentroCusto: createEntity('centros_custo'),
        CustoAlocado: createEntity('custos_alocados'),
        Orcamento: createEntity('orcamentos'),
        ExtratoBancario: createEntity('extratos_bancarios'),
        AtivoImobilizado: createEntity('ativos_imobilizados'),
        ManutencaoAtivo: createEntity('manutencoes_ativo'),
        Financiamento: createEntity('financiamentos'),
        ParcelaFinanciamento: createEntity('parcelas_financiamento'),
        ContaBancaria: createEntity('contas_bancarias'),
        Armazem: createEntity('armazens'),
        EstoqueGrao: createEntity('estoques_grao'),
        MovimentacaoGrao: createEntity('movimentacoes_grao'),
        EstoqueBalanco: createEntity('v_estoque_grao_balanco_unificado'),
        Parceiro: createEntity('parceiros'),
        ParceiroDocumento: createEntity('parceiro_documentos'),
        ContratoComercializacao: createEntity('contratos_comercializacao'),
        ProducaoLeite: createEntity('producao_leite'),
        TanqueLeite: createEntity('tanques_leite'),
        MovimentacaoTanqueLeite: createEntity('movimentacoes_tanque_leite'),
        UnidadeMedida: createEntity('unidades_medida')
    },
    integrations: {
        Core: {
            InvokeLLM: async (payload) => {
                const { data } = await agrogestao.functions.invoke('generate_ai_analysis', payload);
                return data;
            },
            UploadFile: async ({ file, path }) => {
                const ext = file.name.split('.').pop();
                const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${ext}`;
                const filePath = path || `public/${fileName}`;
                const { error } = await client.storage.from('uploads').upload(filePath, file, {
                    cacheControl: '3600',
                    upsert: false
                });
                if (error) throw error;
                const { data: signedData, error: signError } = await client.storage
                    .from('uploads')
                    .createSignedUrl(filePath, 60 * 60 * 24 * 365 * 10); // 10 years
                if (signError) throw signError;
                return {
                    file_url: signedData.signedUrl,
                    path: filePath,
                    name: file.name
                };
            },
            UploadPrivateFile: async ({ file, path }) => {
                const ext = file.name.split('.').pop();
                const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${ext}`;
                const filePath = path || `private/${fileName}`;
                const { error } = await client.storage.from('uploads').upload(filePath, file, {
                    cacheControl: '3600',
                    upsert: false
                });
                if (error) throw error;
                const { data: signedData, error: signError } = await client.storage
                    .from('uploads')
                    .createSignedUrl(filePath, 60 * 60 * 24 * 365 * 10);
                if (signError) throw signError;
                return {
                    file_url: signedData.signedUrl,
                    path: filePath,
                    name: file.name
                };
            },
            CreateFileSignedUrl: async ({ file_url, path, bucket }) => {
                let filePath = path;
                if (file_url && !filePath) {
                    try {
                        const urlObj = new URL(file_url);
                        const pathMatch = urlObj.pathname.match(/\/object\/(?:sign|public)\/uploads\/(.+)/);
                        if (!pathMatch) return { signedUrl: file_url, signed_url: file_url };
                        filePath = decodeURIComponent(pathMatch[1].split('?')[0]);
                    } catch (e) {
                        filePath = file_url;
                    }
                }
                if (!filePath) return { signedUrl: file_url, signed_url: file_url };

                const { data, error } = await client.storage
                    .from(bucket || 'uploads')
                    .createSignedUrl(filePath, 60 * 60 * 24 * 365);
                if (error) throw error;
                return {
                    signedUrl: data.signedUrl,
                    signed_url: data.signedUrl
                };
            },
            ExtractDataFromUploadedFile: async ({ file_url, json_schema }) => {
                const { data } = await agrogestao.functions.invoke('generate_ai_analysis', {
                    prompt: `Analise o documento/imagem no URL fornecido e extraia os dados conforme o schema JSON especificado. Retorne APENAS um JSON válido sem markdown.\n\nURL do arquivo: ${file_url}\n\nSchema esperado: ${JSON.stringify(json_schema)}`,
                    file_url
                });
                if (!data) return { status: 'error', message: 'No data returned' };
                try {
                    const output = typeof data === 'string' ? JSON.parse(data.replace(/```json\n?|\n?```/g, '').trim()) : data;
                    return { status: 'success', output };
                } catch {
                    return { status: 'success', output: data };
                }
            },
            SendEmail: async (payload) => {
                console.log('SendEmail called:', payload);
                return { success: true };
            },
            GenerateImage: async (payload) => {
                console.log('GenerateImage called:', payload);
                return { image_url: '' };
            }
        }
    }
};
